import { useEffect, useState, useMemo } from "react";

/**
 * TypewriterGreeting — cycles through localised greetings on the Home
 * page. Types the current greeting character-by-character, pauses on the
 * full line, then deletes it back down and moves to the next one.
 *
 * Respects `prefers-reduced-motion` — users with the OS-level flag set
 * see the greetings statically, no animation.
 */
export const TypewriterGreeting = ({
  greetings,
  typeSpeedMs = 55,
  deleteSpeedMs = 30,
  holdMs = 2400,
  className = "",
  nameClassName = "text-primary",
  "data-testid": testId = "home-typewriter-greeting",
}) => {
  const [idx, setIdx] = useState(0);
  const [display, setDisplay] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | holding | deleting

  const reduceMotion = useMemo(
    () => typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const current = greetings[idx % greetings.length];
  const fullText = `${current.prefix}, `;

  useEffect(() => {
    if (reduceMotion) {
      setDisplay(fullText);
      return;
    }
    let t;
    if (phase === "typing") {
      if (display.length < fullText.length) {
        t = setTimeout(() => setDisplay(fullText.slice(0, display.length + 1)), typeSpeedMs);
      } else {
        t = setTimeout(() => setPhase("holding"), holdMs);
      }
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("deleting"), holdMs);
    } else {
      if (display.length > 0) {
        t = setTimeout(() => setDisplay(fullText.slice(0, display.length - 1)), deleteSpeedMs);
      } else {
        setIdx((i) => (i + 1) % greetings.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [phase, display, fullText, typeSpeedMs, deleteSpeedMs, holdMs, greetings.length, reduceMotion]);

  return (
    <h1 className={className} data-testid={testId} aria-label={`${current.prefix}, ${current.name}`}>
      {display}
      <span className={nameClassName}>{current.name}</span>
      {!reduceMotion && (
        <span
          className="inline-block w-[2px] h-[0.9em] align-[-0.05em] ml-0.5 bg-primary animate-pulse"
          aria-hidden="true"
        />
      )}
    </h1>
  );
};

export default TypewriterGreeting;
